import { ACCESS_LEVELS } from "../constants";
import { useTranslation } from "react-i18next";

type Props<T extends string> = {
  value: T;
  onChange?: (level: T) => void;
  readOnly?: boolean;
  options?: { value: T; label: string }[];
};

const AccessLevelTabs = <T extends string>({
  value,
  onChange,
  readOnly = false,
  options = ACCESS_LEVELS as { value: T; label: string }[],
}: Props<T>) => {
  const { t } = useTranslation();
  return (
    <div
      role={readOnly ? "group" : "tablist"}
      aria-label={t("mcpPage.fields.accessLevel")}
      className={`inline-flex items-center gap-1 p-1 ${readOnly ? "h-[37px] rounded-xl bg-accent" : "h-10 rounded-[10px] bg-secondary"}`}
    >
      {options.map(({ value: level }) => {
        const isActive = level === value;
        const className = `${readOnly ? "h-[29px] px-2" : "h-8 px-4"} flex items-center justify-center rounded-[8px] text-sm transition-colors ${
          isActive
            ? `bg-background font-semibold text-foreground ${readOnly ? "shadow-sm" : ""}`
            : readOnly
              ? "font-medium text-muted-foreground"
              : "font-medium text-secondary-foreground/70 hover:text-foreground"
        }`;
        return readOnly ? (
          <span key={level} aria-current={isActive ? "true" : undefined} className={className}>
            {t(`mcpPage.accessLevels.${level}`)}
          </span>
        ) : (
          <button key={level} type="button" role="tab" aria-selected={isActive}
            onClick={() => onChange?.(level)} className={className}>
            {t(`mcpPage.accessLevels.${level}`)}
          </button>
        );
      })}
    </div>
  );
};

export default AccessLevelTabs;
